/**
 * useAccountNotes — server state + actions for the Messaging Notes panel.
 *
 * All reads via useQuery, all writes via useMutation (DEVELOPMENT_RULES
 * rule 3); notice is { kind: 'ok' | 'err', text } so success never renders
 * in error red. Facet/search/selection state lives here because filtering
 * is derived from it; per-row UI state (expand, invite, armed) stays in the
 * child components.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MEDDPICC_DIMS } from '@techstack/core';
import apiClient from '../../lib/apiClient';

/** Two-click armed pattern for hard deletes (rule 10). */
export function useArmed() {
  const [armed, setArmed] = useState({});
  const arm = (key, fn) => {
    if (armed[key]) { setArmed((a) => ({ ...a, [key]: false })); fn(); return; }
    setArmed((a) => ({ ...a, [key]: true }));
    setTimeout(() => setArmed((a) => (a[key] ? { ...a, [key]: false } : a)), 3000);
  };
  return [armed, arm];
}

export default function useAccountNotes(account) {
  const queryClient = useQueryClient();
  const base = `/api/v1/account-notes/${encodeURIComponent(account)}`;
  const packsBase = `/api/v1/context-packs/${encodeURIComponent(account)}`;

  const [notice, setNotice] = useState(null);
  const err = (text) => setNotice({ kind: 'err', text });
  const ok = (text) => setNotice({ kind: 'ok', text });

  const [fType, setFType] = useState('');
  const [fSignal, setFSignal] = useState('');
  const [fDim, setFDim] = useState('');
  const [fText, setFText] = useState('');
  const [searchHits, setSearchHits] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState({});

  const notesQuery = useQuery({
    queryKey: ['account-notes', account],
    queryFn: () => apiClient.get(`${base}?limit=50`),
    enabled: !!account,
    staleTime: 0,
  });
  const packsQuery = useQuery({
    queryKey: ['context-packs', account],
    queryFn: () => apiClient.get(packsBase),
    enabled: !!account,
    staleTime: 0,
  });

  const invalidateNotes = () => {
    setSearchHits(null);
    queryClient.invalidateQueries({ queryKey: ['account-notes', account] });
  };
  const invalidatePacks = () => queryClient.invalidateQueries({ queryKey: ['context-packs', account] });

  const mutationDefaults = (fallback, onOk) => ({
    onMutate: () => setNotice(null),
    onError: (e) => err(e?.message || fallback),
    onSuccess: (...args) => { if (onOk) onOk(...args); },
  });

  const saveNote = useMutation({
    mutationFn: ({ content, type, visibility }) => apiClient.post(base, { content, type, visibility }),
    ...mutationDefaults('Save failed — note NOT stored.', () => invalidateNotes()),
  });

  const deleteNote = useMutation({
    mutationFn: (id) => apiClient.del(`/api/v1/account-notes/note/${id}`),
    ...mutationDefaults('Delete failed.', () => invalidateNotes()),
  });

  const changeVis = useMutation({
    mutationFn: ({ id, visibility }) =>
      apiClient.request(`/api/v1/account-notes/note/${id}/visibility`, { method: 'PATCH', body: { visibility } }),
    ...mutationDefaults('Only the author can change visibility.', () => invalidateNotes()),
  });

  const setSharing = useMutation({
    mutationFn: ({ id, emails }) =>
      apiClient.request(`/api/v1/account-notes/note/${id}/sharing`, { method: 'PATCH', body: { sharedWith: emails } }),
    ...mutationDefaults('Sharing change failed.', (_data, { emails }) => {
      invalidateNotes();
      ok(emails.length ? `Shared with ${emails.length} teammate(s).` : 'Invites cleared.');
    }),
  });

  // No title prompt — the consolidated note titles itself (rule 10: no browser prompts).
  const mergeNotes = useMutation({
    mutationFn: (noteIds) => apiClient.post(`${base}/merge`, { noteIds, title: null }),
    ...mutationDefaults('Merge failed.', () => {
      setSelected({});
      invalidateNotes();
      ok('Merged into one consolidated note (sources kept with lineage).');
    }),
  });

  const addToPack = useMutation({
    mutationFn: async ({ packId, name, noteIds }) => {
      let id = packId;
      if (packId === '__new__') {
        const r = await apiClient.post(packsBase, { name: name.trim(), visibility: 'private' });
        id = r.pack.id;
      }
      return apiClient.post(`/api/v1/context-packs/pack/${id}/items`, { noteIds });
    },
    ...mutationDefaults('Add to pack failed.', (data, { noteIds }) => {
      setSelected({});
      invalidatePacks();
      ok(`Added ${data?.added?.length ?? noteIds.length} note(s) to the pack.`);
    }),
  });

  const deletePack = useMutation({
    mutationFn: (packId) => apiClient.del(`/api/v1/context-packs/pack/${packId}`),
    ...mutationDefaults('Only the pack author can delete it.', () => invalidatePacks()),
  });

  const removePackItem = useMutation({
    mutationFn: ({ packId, itemId }) => apiClient.del(`/api/v1/context-packs/pack/${packId}/item/${itemId}`),
    ...mutationDefaults('Remove failed.', () => invalidatePacks()),
  });

  const deepSearch = async () => {
    const q = fText.trim();
    if (!q || searching) return;
    setSearching(true);
    setNotice(null);
    try {
      setSearchHits(await apiClient.get(`${base}/search?q=${encodeURIComponent(q)}&limit=25`));
    } catch (e) {
      err(e?.message || 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  const loadPack = async (packId) => {
    try {
      const r = await apiClient.get(`/api/v1/context-packs/pack/${packId}`);
      return r.pack?.items || [];
    } catch (e) { err(e?.message || 'Pack load failed.'); return null; }
  };

  const copyExport = async (packId, kind) => {
    try {
      const r = await apiClient.get(`/api/v1/context-packs/pack/${packId}/${kind === 'brief' ? 'brief' : 'export'}`);
      await navigator.clipboard.writeText(r.markdown || '');
      ok(kind === 'brief'
        ? `Brief for "${r.name}" copied — coverage, ${r.gaps?.length || 0} gap(s) as discovery questions, next steps.`
        : `Pack "${r.name}" copied as markdown — paste into a doc or NotebookLM.`);
    } catch (e) { err(e?.message || (kind === 'brief' ? 'Brief failed.' : 'Export failed.')); }
  };

  const allNotes = notesQuery.data?.notes || [];
  const notes = searchHits
    ? (searchHits.notes || [])
    : allNotes.filter((n) =>
        (!fType || n.type === fType) &&
        (!fSignal || (n.signals || []).includes(fSignal)) &&
        (!fDim || ((n.meddpicc || {})[fDim] || []).length > 0) &&
        (!fText || (n.content + ' ' + (n.title || '')).toLowerCase().includes(fText.toLowerCase())));

  const dimCounts = {};
  allNotes.forEach((n) => MEDDPICC_DIMS.forEach(([k]) => {
    dimCounts[k] = (dimCounts[k] || 0) + (((n.meddpicc || {})[k]) || []).length;
  }));

  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);

  return {
    // data
    allNotes, notes, dimCounts,
    packs: packsQuery.data?.packs || [],
    isLoading: notesQuery.isLoading,
    loadError: notesQuery.error,
    // notice
    notice, err, ok,
    // facets + search
    fType, setFType, fSignal, setFSignal, fDim, setFDim, fText, setFText,
    searchHits, setSearchHits, searching, deepSearch,
    // selection
    selected, selectedIds, toggleSelect, clearSelection: () => setSelected({}),
    // mutations + pack reads
    saveNote, deleteNote, changeVis, setSharing, mergeNotes,
    addToPack, deletePack, removePackItem, loadPack, copyExport,
  };
}
